/**
 * The console's zod schemas. Pure zod — no React, no axios — so they are unit-testable on
 * their own, the same discipline `lib/validation.js` sets.
 *
 * A `message` IS AN I18N KEY, NEVER PROSE. A schema is module scope: it cannot call a hook
 * and has no language. The component resolves the key with its own `t` at render time. This
 * is enforced by `src/__tests__/validationKeys.static.test.js`, which scans every literal
 * passed to `.min()`, `.regex()` and friends and fails if it does not exist in a dictionary.
 *
 * Every bound MIRRORS THE SERVER exactly, so the operator is never told by a 422 what the
 * form could have told them first — and the server still refuses anything that skips the UI,
 * because a client-side guard is a suggestion, not a rule.
 */
import { z } from "zod";

/** `admin.create_adjustment`: an integer amount, non-zero (the SIGN is the direction), and a
 *  reason of at least 5 characters after stripping. */
export const adjustmentSchema = z.object({
  tenantId: z.string().trim().min(1, "gw.valid.tenantRequired").max(64, "validation.tooLong"),
  direction: z.enum(["credit", "debit"], {
    errorMap: () => ({ message: "gw.valid.directionRequired" }),
  }),
  // A STRING, refused by pattern before any `Number()` runs. `toCents("abc")` answers 0, not
  // NaN (`admin/money.js` does `Number(major || 0)`), so a garbage amount would otherwise
  // reach the wire as a legal-looking zero — which the server then refuses as
  // ADJUSTMENT_INVALID for a reason the operator cannot see on the form.
  amount: z.string().trim()
    .min(1, "gw.valid.amountRequired")
    .regex(/^\d+(\.\d{1,2})?$/, "gw.valid.amountFormat")
    .refine((v) => Number(v) > 0, "gw.valid.amountNonZero"),
  reason: z.string().trim().min(5, "gw.valid.reasonMin5").max(500, "validation.tooLong"),
});

/** `commission.upsert_rule` + `CommissionRule._sane`: a percentage in 0..100 (stored as
 *  0..10000 bps), a tenant id when the scope is one practice, and nothing else. */
export const commissionRuleSchema = z.object({
  scope: z.enum(["platform", "tenant"]),
  tenantId: z.string().trim(),
  ratePct: z.string().trim()
    .min(1, "gw.valid.rateRequired")
    .regex(/^\d{1,3}(\.\d{1,2})?$/, "gw.valid.rateFormat")
    .refine((v) => Number(v) <= 100, "gw.valid.rateRange"),
}).superRefine((v, ctx) => {
  if (v.scope === "tenant" && !v.tenantId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tenantId"],
                   message: "gw.valid.tenantRequired" });
  }
});

export default { adjustmentSchema, commissionRuleSchema };
